import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ScanText, Sparkles, Brain, CircleHelp, CheckCircle2,
  ArrowRight, Loader2, FileJson, Boxes, Layers, Tag, Shield,
  Maximize2, X,
} from "lucide-react";
import foyrSample from "../../data/samples/foyr-classic-living-office.json";
import { NodeKindIcon } from "../integrations/NodeKindIcon";
import { cls } from "../../lib/cls";

/* OnboardingMagic — when the designer loads the Foyr sample and
 * presses "Read the brief", we don't dump the parsed plan all at
 * once. We *show* the agent's reasoning step by step, anchored to
 * the real facts in the sample (CLAUDE.md §1 — no canned strings):
 *
 *   1. parse        → JSON shape detected, schema version read
 *   2. provenance   → "synthesised from public page" badge
 *   3. extract      → rooms, items, placements counted
 *   4. type-map     → each known field → ontology kind
 *   5. confidence   → which fields landed high vs need designer
 *   6. clarify      → the exact missing-fields list
 *   7. ready        → "create the project" CTA armed
 *
 * Each line is pulled live from foyrSample so it stays true even if
 * the sample is regenerated. */

interface Step {
  id: string;
  icon: JSX.Element;
  title: string;
  detail: string;
  pill?: string;
}

function tally<T>(arr: T[] | null | undefined): number {
  return Array.isArray(arr) ? arr.length : 0;
}

function buildSteps(): Step[] {
  const sample = foyrSample as {
    schema: string;
    provenance: { source_kind: string; source_url: string; retrieved_on: string };
    project: { title_observed: string; style_tag_observed_from_title: string; category_breadcrumb_observed: string[] };
    rooms: unknown[];
    items_summary: { count_reported_on_page: number; count_observed_in_visible_thumbnails: number };
    items: unknown[];
    placements_observed_from_render: unknown[];
    missing_fields_to_ask: string[];
  };

  const itemsCount = tally(sample.items);
  const placementsCount = tally(sample.placements_observed_from_render);
  const missing = tally(sample.missing_fields_to_ask);

  return [
    {
      id: "parse",
      icon: <FileJson className="w-3.5 h-3.5" />,
      title: "Read the file",
      detail: `Schema ${sample.schema} · ${sample.provenance.source_kind}`,
      pill: "JSON · 1 file",
    },
    {
      id: "provenance",
      icon: <Shield className="w-3.5 h-3.5" />,
      title: "Stamp provenance",
      detail: `Source · ${sample.provenance.source_url} · retrieved ${sample.provenance.retrieved_on}`,
      pill: "honest-trace",
    },
    {
      id: "title",
      icon: <Tag className="w-3.5 h-3.5" />,
      title: "Read the title",
      detail: `"${sample.project.title_observed}" → style tag = ${sample.project.style_tag_observed_from_title}`,
      pill: "1 Intent",
    },
    {
      id: "breadcrumb",
      icon: <Layers className="w-3.5 h-3.5" />,
      title: "Read the breadcrumb",
      detail: sample.project.category_breadcrumb_observed.join(" → "),
      pill: "category",
    },
    {
      id: "rooms",
      icon: <Layers className="w-3.5 h-3.5" />,
      title: "Type rooms",
      detail: `${tally(sample.rooms)} room — living-cum-home-office, area = null (designer-confirm)`,
      pill: `${tally(sample.rooms)} Room`,
    },
    {
      id: "items",
      icon: <Boxes className="w-3.5 h-3.5" />,
      title: "Type items",
      detail: `${itemsCount} of ${sample.items_summary.count_reported_on_page} visible — vendor / price / lead = null (designer-confirm)`,
      pill: `${itemsCount} SKU`,
    },
    {
      id: "placements",
      icon: <Brain className="w-3.5 h-3.5" />,
      title: "Type placements",
      detail: `${placementsCount} typed placements from the rendered scene · zones tagged · z-height null`,
      pill: `${placementsCount} Placement`,
    },
    {
      id: "missing",
      icon: <CircleHelp className="w-3.5 h-3.5" />,
      title: "Flag the gaps",
      detail: `${missing} fields the page can't tell us — we'll ask, never silently default.`,
      pill: `${missing} ask`,
    },
    {
      id: "ready",
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      title: "Ready to create",
      detail: "Confidence-gated · no fabricated values · designer confirms anything below the floor",
      pill: "✓ green-light",
    },
  ];
}

const STEPS = buildSteps();

interface OnboardingMagicProps {
  /** Triggers the play. Pass a unique key (e.g. plan_id) to restart. */
  playKey?: string | null;
  /** Called with the project name the designer chose. The host is
   *  responsible for the actual commit (localStorage + nav). */
  onCreate?: (projectName: string) => void;
}

export function OnboardingMagic({ playKey, onCreate }: OnboardingMagicProps): JSX.Element | null {
  const sample = foyrSample as { project: { title_observed: string } };
  const [running, setRunning] = useState(false);
  const [shown, setShown] = useState<number>(0);
  const defaultName = sample.project.title_observed
    ? sample.project.title_observed.replace(/\b\w/g, (c) => c.toUpperCase())
    : "New Foyr project";
  const [projectName, setProjectName] = useState<string>(defaultName);

  useEffect(() => {
    if (!playKey) { setRunning(false); setShown(0); return; }
    setRunning(true); setShown(0);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setShown(i);
      if (i >= STEPS.length) {
        window.clearInterval(id);
        setRunning(false);
      }
    }, 280);
    return () => window.clearInterval(id);
  }, [playKey]);

  if (!playKey) return null;

  const visible = STEPS.slice(0, shown);

  return (
    <section className="panel-hero p-5 sm:p-6 mt-4 relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-20 -right-12 w-72 h-72 rounded-full bg-foyr/12 blur-3xl pointer-events-none" />
      <div className="relative">
        <header className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-ink-950 text-cream grid place-items-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <div>
              <div className="eyebrow">Onboarding agent · live</div>
              <div className="display-3 text-ink-950 leading-tight">
                Watching the agent map your sample into the typed graph.
              </div>
            </div>
          </div>
          {running ? (
            <span className="pill bg-foyr/10 text-foyr-deep inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> mapping…
            </span>
          ) : (
            <span className="pill bg-fern/10 text-fern inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> done
            </span>
          )}
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          <ol className="relative pl-7 sm:pl-8">
            <span aria-hidden className="absolute left-[10px] sm:left-[11px] top-1 bottom-1 w-px bg-gradient-to-b from-foyr/60 via-border to-fern/40" />
            <AnimatePresence initial={false}>
              {visible.map((s, i) => (
                <motion.li
                  key={s.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22, delay: 0.02 }}
                  className="relative pb-2.5 last:pb-0"
                >
                  <span aria-hidden className={cls(
                    "absolute -left-7 sm:-left-8 top-1 w-5 h-5 rounded-full grid place-items-center bg-cream border-2",
                    i < shown - 1 ? "border-fern text-fern shadow-[0_0_0_3px_rgba(63,122,85,0.08)]"
                                   : "border-foyr text-foyr-deep shadow-[0_0_0_3px_rgba(201,92,26,0.10)]",
                  )}>
                    {s.icon}
                  </span>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="text-[13px] font-semibold text-ink-950 leading-tight">{s.title}</div>
                    {s.pill ? (
                      <span className="pill text-[10px] tabular bg-cream-dark border border-border text-ink-700">
                        {s.pill}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-[11.5px] text-ink-700 mt-0.5 italic leading-snug">{s.detail}</div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ol>

          <TypedGraphPreview shown={shown} />
        </div>

        {!running && shown >= STEPS.length ? (
          <div className="mt-4 rounded-xl border border-foyr/30 bg-foyr/5 px-3.5 py-3 space-y-3">
            <div className="text-[12px] text-ink-700 leading-snug">
              <span className="font-semibold text-ink-950">All steps cited.</span> Designer confirms{" "}
              <span className="font-mono text-foyr-deep">{(foyrSample as { missing_fields_to_ask: string[] }).missing_fields_to_ask.length}</span>{" "}
              gap fields → project ingests into the cockpit, every value with provenance.
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); if (onCreate && projectName.trim()) onCreate(projectName.trim()); }}
              className="flex items-center gap-2 flex-wrap"
            >
              <label className="flex-1 min-w-[220px]">
                <span className="block text-[10.5px] uppercase tracking-wider font-mono text-ink-500 mb-1">Name your project</span>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. Mehta · Indiranagar Loft"
                  className="w-full bg-cream border border-border rounded-lg px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-foyr/30"
                  required
                />
              </label>
              <button
                type="submit"
                disabled={!onCreate || !projectName.trim()}
                className="btn btn-foyr !text-sm !py-2 !px-3 inline-flex items-center gap-1.5 self-end"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                Create the project
              </button>
            </form>
          </div>
        ) : null}
      </div>
      <ScanText className="hidden" />
    </section>
  );
}

/* TypedGraphPreview — the right-hand pane that materializes typed
 * nodes node-by-node as the timeline steps fire. Each kind lights
 * up at the matching step number so the picture builds in front
 * of the designer's eyes. Pure SVG/CSS, no xyflow. */

interface PreviewNode {
  id: string;
  label: string;
  kind: string;
  appearsAtStep: number;
  x: number;
  y: number;
  tone: "ink" | "sea" | "foyr" | "fern";
}

const PREVIEW_NODES: PreviewNode[] = [
  { id: "p",  label: "Project",   kind: "Project",   appearsAtStep: 1, x: 50, y: 22, tone: "ink"  },
  { id: "in", label: "Intent",    kind: "Intent",    appearsAtStep: 3, x: 20, y: 50, tone: "foyr" },
  { id: "br", label: "Category",  kind: "Breadcrumb",appearsAtStep: 4, x: 78, y: 50, tone: "sea"  },
  { id: "r",  label: "Room",      kind: "Room",      appearsAtStep: 5, x: 20, y: 78, tone: "sea"  },
  { id: "sk", label: "SKUs · 15", kind: "SKU",       appearsAtStep: 6, x: 50, y: 86, tone: "foyr" },
  { id: "pl", label: "Placements",kind: "Placement", appearsAtStep: 7, x: 80, y: 78, tone: "foyr" },
  { id: "q",  label: "Questions", kind: "Ask",       appearsAtStep: 8, x: 50, y: 60, tone: "ink"  },
];

const PREVIEW_EDGES: { from: string; to: string; appearsAtStep: number }[] = [
  { from: "p",  to: "in", appearsAtStep: 3 },
  { from: "p",  to: "br", appearsAtStep: 4 },
  { from: "p",  to: "r",  appearsAtStep: 5 },
  { from: "r",  to: "sk", appearsAtStep: 6 },
  { from: "r",  to: "pl", appearsAtStep: 7 },
  { from: "sk", to: "q",  appearsAtStep: 8 },
  { from: "pl", to: "q",  appearsAtStep: 8 },
];

const TONE_BG: Record<PreviewNode["tone"], string> = {
  ink:  "bg-ink-950 text-cream",
  sea:  "bg-sky-50 text-sky-800 border border-sky-200",
  foyr: "bg-foyr/10 text-foyr-deep border border-foyr/30",
  fern: "bg-fern/10 text-fern border border-fern/30",
};

function TypedGraphPreview({ shown }: { shown: number }): JSX.Element {
  const visibleNodes = PREVIEW_NODES.filter((n) => n.appearsAtStep <= shown);
  const visibleEdges = PREVIEW_EDGES.filter((e) => e.appearsAtStep <= shown);
  const nodeById = new Map(PREVIEW_NODES.map((n) => [n.id, n] as const));
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setExpanded(false); }
    if (expanded) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    }
    return () => { /* no-op */ };
  }, [expanded]);

  const graphCanvas = (large: boolean) => (
    <div className={cls("relative w-full", large ? "h-full" : "aspect-[4/3]")}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
        <AnimatePresence>
          {visibleEdges.map((e) => {
            const a = nodeById.get(e.from);
            const b = nodeById.get(e.to);
            if (!a || !b) return null;
            return (
              <motion.line
                key={`${e.from}-${e.to}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke="#C95C1A" strokeOpacity="0.55" strokeWidth={large ? 0.25 : 0.4}
                strokeDasharray="1.2 0.8"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              />
            );
          })}
        </AnimatePresence>
      </svg>

      <AnimatePresence>
        {visibleNodes.map((n) => (
          <motion.div
            key={n.id}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 320, damping: 22, mass: 0.6 }}
            className={cls(
              "absolute rounded-xl py-1 font-semibold leading-tight shadow-[0_2px_8px_-4px_rgba(0,0,0,0.18)] inline-flex items-center gap-1.5",
              large ? "pl-2 pr-3 text-[13px]" : "pl-1.5 pr-2.5 text-[10.5px]",
              TONE_BG[n.tone],
            )}
            style={{
              left: `${n.x}%`,
              top:  `${n.y}%`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <span className={cls("rounded-md bg-cream/50 grid place-items-center shrink-0", large ? "w-7 h-7" : "w-5 h-5")}>
              <NodeKindIcon kind={n.kind} size={large ? 16 : 12} stroke={1.7} />
            </span>
            <span>{n.label}</span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );

  return (
    <aside className="rounded-2xl border border-border/60 bg-cream/70 backdrop-blur-sm p-3 sm:p-4 relative overflow-hidden min-h-[300px]">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">Typed graph · materializing</div>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Expand"
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-cream-dark border border-border text-[10.5px] text-ink-700 hover:text-ink-950 hover:border-foyr/40 transition"
        >
          <Maximize2 className="w-3 h-3" /> Expand
        </button>
      </div>
      {graphCanvas(false)}
      <div className="mt-3 flex items-center justify-between text-[10.5px] text-ink-500 font-mono uppercase tracking-wider">
        <span>{visibleNodes.length} typed nodes</span>
        <span>{visibleEdges.length} edges</span>
      </div>

      {expanded ? createPortal((
        <AnimatePresence>
          <motion.div
            key="bg"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-ink-950/40 backdrop-blur-sm"
            onClick={() => setExpanded(false)}
          />
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-4 sm:inset-8 z-[81] panel overflow-hidden flex flex-col bg-cream"
          >
            <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-border/60">
              <div>
                <div className="eyebrow">Typed graph · expanded</div>
                <div className="display-3 text-ink-950 leading-tight">
                  {visibleNodes.length} typed nodes · {visibleEdges.length} edges
                </div>
              </div>
              <button onClick={() => setExpanded(false)} aria-label="Close" className="w-8 h-8 rounded-lg bg-cream-dark border border-border grid place-items-center text-ink-700 hover:text-ink-950 transition">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="flex-1 bg-[radial-gradient(circle_at_50%_50%,rgba(201,92,26,0.04),transparent_70%)] p-6 sm:p-10">
              {graphCanvas(true)}
            </div>
          </motion.div>
        </AnimatePresence>
      ), document.body) : null}
    </aside>
  );
}
